import { Controller } from "@hotwired/stimulus"
import L from "leaflet"

// Multi-stop color palettes — 5 stops from -1 to +1 for rich color variety
// Price: deep red → coral → pale gold → lime green → deep emerald
const PRICE_STOPS = [
  { pos: -1.0, rgb: [220, 38, 38]  },  // red-600
  { pos: -0.4, rgb: [251, 113, 67] },   // vibrant coral
  { pos:  0.0, rgb: [254, 240, 185] },  // pale gold (clean neutral)
  { pos:  0.4, rgb: [74, 222, 128]  },  // green-400
  { pos:  1.0, rgb: [5, 150, 105]   }   // emerald-600
]
// Inventory: deep red → coral → pale gold → sky blue → bold blue
const INVENTORY_STOPS = [
  { pos: -1.0, rgb: [220, 38, 38]  },   // red-600
  { pos: -0.4, rgb: [251, 113, 67] },    // vibrant coral
  { pos:  0.0, rgb: [254, 240, 185] },   // pale gold
  { pos:  0.4, rgb: [56, 189, 248]  },   // sky-400
  { pos:  1.0, rgb: [37, 99, 235]   }    // blue-600
]

export default class extends Controller {
  static targets = [
    "mapContainer", "searchInput", "searchResults",
    "viewType", "metric", "period",
    "legend", "legendMin", "legendMax", "legendLabel",
    "infoPanel", "infoPanelBody", "loading", "emptyState",
    "parentGeo", "progressBar", "progressFill", "progressPct"
  ]

  static values = {
    geographyId: Number,
    geographyName: String
  }

  connect() {
    this.markers = []
    this.markerGroups = { rising: [], dropping: [], neutral: [] }
    this.visibleGroups = { rising: true, dropping: true, neutral: false }
    this.searchTimeout = null
    this.lastData = null
    this.lastAbsMax = null
    this.initMap()

    if (this.geographyIdValue) {
      this.loadMapData()
    }
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  initMap() {
    this.map = L.map(this.mapContainerTarget, {
      center: [39.8283, -98.5795], // Center of US
      zoom: 4,
      zoomControl: true
    })

    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>',
      maxZoom: 19,
      subdomains: "abcd"
    }).addTo(this.map)
  }

  // Search functionality
  onSearchInput() {
    clearTimeout(this.searchTimeout)
    const query = this.searchInputTarget.value.trim()

    if (query.length < 2) {
      this.hideSearchResults()
      return
    }

    this.searchTimeout = setTimeout(() => this.performSearch(query), 300)
  }

  onSearchKeydown(event) {
    if (event.key === "Escape") {
      this.hideSearchResults()
    }
    if (event.key === "Enter") {
      this.onSearchSubmit()
    }
  }

  onSearchSubmit() {
    const first = this.searchResultsTarget.querySelector(".map-search-result-item")
    if (first) {
      first.click()
    }
  }

  async performSearch(query) {
    try {
      const response = await fetch(`/api/geographies/search?q=${encodeURIComponent(query)}`)
      const results = await response.json()

      // Filter to MSAs and cities only (geographies that have children)
      const mapable = results.filter(r => r.geo_type === "msa" || r.geo_type === "city")

      if (mapable.length === 0) {
        this.searchResultsTarget.innerHTML = '<div class="map-search-no-results">No MSAs or cities found</div>'
        this.searchResultsTarget.style.display = "block"
        return
      }

      const html = mapable.map(r => {
        const badge = r.geo_type === "msa" ? "MSA" : "City"
        return `<div class="map-search-result-item" data-action="click->heat-map#selectGeography" data-id="${r.id}" data-name="${this.escapeHtml(r.display_name)}">
          <span class="map-search-result-badge map-search-badge-${r.geo_type}">${badge}</span>
          <span class="map-search-result-name">${this.escapeHtml(r.display_name)}</span>
        </div>`
      }).join("")

      this.searchResultsTarget.innerHTML = html
      this.searchResultsTarget.style.display = "block"
    } catch (e) {
      console.error("Search error:", e)
    }
  }

  selectGeography(event) {
    const item = event.currentTarget
    const id = parseInt(item.dataset.id)
    const name = item.dataset.name

    this.geographyIdValue = id
    this.geographyNameValue = name
    this.searchInputTarget.value = name
    this.hideSearchResults()

    // Update URL without reload
    const url = new URL(window.location)
    url.searchParams.set("geography_id", id)
    window.history.pushState({}, "", url)

    this.loadMapData()
  }

  updateParentGeo(data) {
    if (!this.hasParentGeoTarget) return

    if (data && data.geography) {
      const geo = data.geography

      // Sync the search input and URL to the resolved MSA
      this.geographyIdValue = geo.id
      this.geographyNameValue = geo.name
      this.searchInputTarget.value = geo.name
      const url = new URL(window.location)
      url.searchParams.set("geography_id", geo.id)
      window.history.replaceState({}, "", url)

      this.parentGeoTarget.innerHTML = `
        <span class="geo-chip" style="border-color: #6ABF4B">
          <span class="geo-chip-dot" style="background: #6ABF4B"></span>
          <span class="geo-chip-label">${this.escapeHtml(geo.name)}</span>
          <button class="geo-chip-remove" data-action="click->heat-map#clearGeography" title="Remove">&times;</button>
        </span>
      `
      this.parentGeoTarget.style.display = ""
    } else {
      this.parentGeoTarget.style.display = "none"
    }
  }

  clearGeography() {
    this.geographyIdValue = 0
    this.geographyNameValue = ""
    this.searchInputTarget.value = ""
    this.clearMarkers()
    this.hideLegend()
    if (this.hasInfoPanelTarget) this.infoPanelTarget.style.display = "none"
    if (this.hasParentGeoTarget) this.parentGeoTarget.style.display = "none"
    this.showEmpty("Search for an MSA or city above to view the heat map.")

    const url = new URL(window.location)
    url.searchParams.delete("geography_id")
    window.history.pushState({}, "", url)
  }

  hideSearchResults() {
    this.searchResultsTarget.style.display = "none"
  }

  // Control changes
  onViewTypeChange() {
    this.loadMapData()
  }

  onMetricChange() {
    this.loadMapData()
  }

  onPeriodChange() {
    this.loadMapData()
  }

  // Load map data from API
  async loadMapData() {
    if (!this.geographyIdValue) return

    this.showLoading()

    const viewType = this.viewTypeTarget.value
    const metric = this.metricTarget.value
    const period = this.periodTarget.value

    try {
      const url = `/api/map_data?geography_id=${this.geographyIdValue}&view_type=${viewType}&metric=${metric}&period=${period}`
      const response = await fetch(url)

      if (!response.ok) throw new Error("Failed to load map data")

      const data = await response.json()

      // Client-side geocode any children missing coordinates
      const needGeocode = (data.children || []).filter(c => !c.latitude && c.geocode_query)
      if (needGeocode.length > 0) {
        this.showLoadingMessage(`Resolving coordinates for ${needGeocode.length} locations (one-time)...`)
        this.showProgress(0, needGeocode.length)
        await this.geocodeChildren(needGeocode)
      }

      this.renderMap(data)
    } catch (e) {
      console.error("Map data error:", e)
      this.hideLoading()
      this.showEmpty("Failed to load map data. Please try again.")
    }
  }

  renderMap(data) {
    // Clear existing markers
    this.clearMarkers()

    const children = data.children || []

    this.updateParentGeo(data)

    if (children.length === 0) {
      this.showEmpty(
        this.geographyIdValue
          ? "No sub-market data found for this geography."
          : "Search for an MSA or city above to view the heat map."
      )
      this.hideLegend()
      return
    }

    const geocoded = children.filter(c => c.latitude && c.longitude)
    const notGeocoded = children.length - geocoded.length

    if (geocoded.length === 0) {
      this.showEmpty(
        `Found ${children.length} sub-markets but coordinates are not yet available.<br>` +
        `Try switching to Cities view, or reload to retry geocoding.`
      )
      this.hideLegend()
      this.updateInfoPanel(data, children)
      return
    }

    this.hideEmpty()
    this.hideLoading()

    // Compute color scale — use percentile clamping so outliers don't compress everything to neutral
    const values = children.filter(c => c.change_value != null).map(c => c.change_value)
    const absValues = values.map(v => Math.abs(v)).sort((a, b) => a - b)
    const rawMax = Math.max(Math.abs(Math.min(...values, 0)), Math.abs(Math.max(...values, 0)), 0.01)
    // Clamp at 85th percentile so the top 15% of outliers don't wash out the entire scale
    const p85Index = Math.min(Math.floor(absValues.length * 0.85), absValues.length - 1)
    const p85 = absValues.length > 0 ? absValues[p85Index] : rawMax
    const absMax = Math.max(p85, rawMax * 0.25, 0.5) // ensure minimum spread

    // Update legend
    this.showLegend(data.scale, data.metric, absMax)
    if (notGeocoded > 0) {
      this.showGeocodeBanner(notGeocoded, children.length)
    } else {
      this.hideGeocodeBanner()
    }

    // Store data for toggle redraws
    this.lastData = data
    this.lastAbsMax = absMax
    this.lastGeocoded = geocoded

    // Categorize and add markers
    this.markerGroups = { rising: [], dropping: [], neutral: [] }
    const bounds = []
    geocoded.forEach(child => {
      const latlng = [child.latitude, child.longitude]
      const color = this.getColor(child.change_value, absMax, data.metric)
      const radius = this.getRadius(child)

      const category = child.change_value > 0 ? "rising"
        : child.change_value < 0 ? "dropping"
        : "neutral"

      const circle = L.circleMarker(latlng, {
        radius: radius,
        fillColor: color,
        color: color,
        weight: 1.5,
        opacity: 0.5,
        fillOpacity: 0.55,
        className: "heat-marker"
      })

      circle.bindPopup(this.buildPopup(child, data))
      circle.on("mouseover", function() { this.openPopup() })
      circle.on("mouseout", function() { this.closePopup() })

      this.markerGroups[category].push(circle)
      this.markers.push(circle)

      // Only add to map if this category is visible
      if (this.visibleGroups[category]) {
        circle.addTo(this.map)
        bounds.push(latlng)
      }
    })

    // Fit map to visible markers only
    if (bounds.length > 0) {
      this.map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 })
    }

    // Update info panel with toggle buttons
    this.updateInfoPanel(data, children)
  }

  getColor(value, absMax, metric) {
    if (value == null) return "#cbd5e1"  // slate-300 for missing data

    // Normalize to -1..1, then apply power curve (sqrt) to spread middle values
    const linear = Math.max(-1, Math.min(1, value / absMax))
    const normalized = Math.sign(linear) * Math.pow(Math.abs(linear), 0.45)

    const stops = metric === "inventory" ? INVENTORY_STOPS : PRICE_STOPS
    return this.interpolateStops(stops, normalized)
  }

  interpolateStops(stops, t) {
    // Clamp t and find the two surrounding stops
    t = Math.max(stops[0].pos, Math.min(stops[stops.length - 1].pos, t))

    for (let i = 0; i < stops.length - 1; i++) {
      if (t <= stops[i + 1].pos) {
        const range = stops[i + 1].pos - stops[i].pos
        const localT = range > 0 ? (t - stops[i].pos) / range : 0
        const from = stops[i].rgb
        const to = stops[i + 1].rgb
        const r = Math.round(from[0] + (to[0] - from[0]) * localT)
        const g = Math.round(from[1] + (to[1] - from[1]) * localT)
        const b = Math.round(from[2] + (to[2] - from[2]) * localT)
        return `rgb(${r},${g},${b})`
      }
    }
    const last = stops[stops.length - 1].rgb
    return `rgb(${last[0]},${last[1]},${last[2]})`
  }

  getRadius(child) {
    if (child.population) {
      if (child.population > 500000) return 32
      if (child.population > 100000) return 26
      if (child.population > 50000) return 22
      if (child.population > 10000) return 18
      return 14
    }
    return child.geo_type === "city" ? 20 : 14
  }

  buildPopup(child, data) {
    const metricLabel = data.metric === "inventory" ? "Inventory" : "Price"
    const changeLabel = child.change_value != null
      ? (child.change_value >= 0 ? "+" : "") + child.change_value.toFixed(2) + (data.metric === "inventory" ? " /1K" : "%")
      : "N/A"
    const changeClass = child.change_value > 0 ? "map-popup-positive" : child.change_value < 0 ? "map-popup-negative" : ""

    let currentFormatted = "N/A"
    if (child.current_value != null) {
      if (data.metric === "inventory") {
        currentFormatted = child.current_value.toLocaleString() + " listings"
      } else {
        currentFormatted = "$" + child.current_value.toLocaleString()
      }
    }

    let marketBadge = ""
    if (child.market_type) {
      const label = child.market_type === "sellers" ? "Seller's" : child.market_type === "balanced" ? "Balanced" : "Buyer's"
      marketBadge = `<span class="map-popup-market-badge map-popup-market-${child.market_type}">${label}</span>`
    }

    return `
      <div class="map-popup">
        <div class="map-popup-title">${this.escapeHtml(child.name)}</div>
        ${marketBadge}
        <div class="map-popup-row">
          <span class="map-popup-label">Current ${metricLabel}:</span>
          <span class="map-popup-value">${currentFormatted}</span>
        </div>
        <div class="map-popup-row">
          <span class="map-popup-label">Change:</span>
          <span class="map-popup-value ${changeClass}">${changeLabel}</span>
        </div>
        ${child.population ? `<div class="map-popup-row"><span class="map-popup-label">Population:</span><span class="map-popup-value">${child.population.toLocaleString()}</span></div>` : ""}
        ${child.inv_per_1k != null ? `<div class="map-popup-row"><span class="map-popup-label">Inv/1K Pop:</span><span class="map-popup-value">${child.inv_per_1k}</span></div>` : ""}
      </div>
    `
  }

  updateInfoPanel(data, children) {
    if (!this.hasInfoPanelBodyTarget) return

    const rising = children.filter(c => c.change_value > 0).length
    const dropping = children.filter(c => c.change_value < 0).length
    const neutral = children.filter(c => c.change_value == null || c.change_value === 0).length

    const sellers = children.filter(c => c.market_type === "sellers").length
    const balanced = children.filter(c => c.market_type === "balanced").length
    const buyers = children.filter(c => c.market_type === "buyers").length

    const geoLabel = data.view_type === "zips" ? "Zip Codes" : "Cities"

    this.infoPanelBodyTarget.innerHTML = `
      <div class="map-info-stat map-info-toggle ${this.visibleGroups.rising ? 'active' : ''}" data-group="rising" data-action="click->heat-map#toggleGroup">
        <span class="map-info-number map-info-rising">${rising}</span>
        <span class="map-info-label">Rising</span>
      </div>
      <div class="map-info-stat map-info-toggle ${this.visibleGroups.dropping ? 'active' : ''}" data-group="dropping" data-action="click->heat-map#toggleGroup">
        <span class="map-info-number map-info-dropping">${dropping}</span>
        <span class="map-info-label">Dropping</span>
      </div>
      <div class="map-info-stat map-info-toggle ${this.visibleGroups.neutral ? 'active' : ''}" data-group="neutral" data-action="click->heat-map#toggleGroup">
        <span class="map-info-number">${neutral}</span>
        <span class="map-info-label">Neutral</span>
      </div>
      ${sellers + balanced + buyers > 0 ? `
        <div class="map-info-divider"></div>
        <div class="map-info-stat"><span class="map-info-number">${sellers}</span><span class="map-info-label">Seller's</span></div>
        <div class="map-info-stat"><span class="map-info-number">${balanced}</span><span class="map-info-label">Balanced</span></div>
        <div class="map-info-stat"><span class="map-info-number">${buyers}</span><span class="map-info-label">Buyer's</span></div>
      ` : ""}
    `
    this.infoPanelTarget.style.display = "block"
  }

  toggleGroup(event) {
    const group = event.currentTarget.dataset.group
    this.visibleGroups[group] = !this.visibleGroups[group]

    // Toggle the active class on the clicked element
    event.currentTarget.classList.toggle("active", this.visibleGroups[group])

    // Show/hide markers in this group
    const markers = this.markerGroups[group] || []
    markers.forEach(marker => {
      if (this.visibleGroups[group]) {
        marker.addTo(this.map)
      } else {
        marker.remove()
      }
    })
  }

  showLegend(scale, metric, absMax) {
    if (!this.hasLegendTarget) return
    this.legendTarget.style.display = "flex"

    // Apply the correct gradient for the metric
    const gradientEl = this.legendTarget.querySelector(".map-legend-gradient")
    if (gradientEl) {
      gradientEl.classList.remove("map-legend-gradient-price", "map-legend-gradient-inventory")
      gradientEl.classList.add(metric === "inventory" ? "map-legend-gradient-inventory" : "map-legend-gradient-price")
    }

    if (metric === "inventory") {
      this.legendMinTarget.textContent = "-" + absMax.toFixed(1) + "/1K"
      this.legendMaxTarget.textContent = "+" + absMax.toFixed(1) + "/1K"
      this.legendLabelTarget.textContent = "Inventory Change"
    } else {
      this.legendMinTarget.textContent = "-" + absMax.toFixed(1) + "%"
      this.legendMaxTarget.textContent = "+" + absMax.toFixed(1) + "%"
      this.legendLabelTarget.textContent = "Price Change"
    }
  }

  hideLegend() {
    if (this.hasLegendTarget) this.legendTarget.style.display = "none"
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.style.display = "flex"
      const msg = this.loadingTarget.querySelector("p")
      if (msg) msg.textContent = "Loading map data..."
    }
    this.hideProgress()
    this.hideEmpty()
  }

  showLoadingMessage(text) {
    if (this.hasLoadingTarget) {
      this.loadingTarget.style.display = "flex"
      const msg = this.loadingTarget.querySelector("p")
      if (msg) msg.textContent = text
    }
  }

  showProgress(current, total) {
    const pct = Math.round((current / total) * 100)
    if (this.hasProgressBarTarget) this.progressBarTarget.style.display = "flex"
    if (this.hasProgressFillTarget) this.progressFillTarget.style.width = pct + "%"
    if (this.hasProgressPctTarget) this.progressPctTarget.textContent = pct + "%"
  }

  hideProgress() {
    if (this.hasProgressBarTarget) this.progressBarTarget.style.display = "none"
    if (this.hasProgressFillTarget) this.progressFillTarget.style.width = "0%"
  }

  hideLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.style.display = "none"
    this.hideProgress()
  }

  showEmpty(message) {
    this.hideLoading()
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.innerHTML = `<p>${message}</p>`
      this.emptyStateTarget.style.display = "flex"
    }
  }

  hideEmpty() {
    if (this.hasEmptyStateTarget) this.emptyStateTarget.style.display = "none"
  }

  showGeocodeBanner(missing, total) {
    let banner = this.element.querySelector(".map-geocode-banner")
    if (!banner) {
      banner = document.createElement("div")
      banner.className = "map-geocode-banner"
      this.mapContainerTarget.parentElement.prepend(banner)
    }
    banner.innerHTML = `Showing ${total - missing} of ${total} sub-markets. ${missing} could not be geocoded.`
    banner.style.display = "block"
  }

  hideGeocodeBanner() {
    const banner = this.element.querySelector(".map-geocode-banner")
    if (banner) banner.style.display = "none"
  }

  // Geocode children missing coordinates using Nominatim from the browser.
  // Nominatim requires max 1 req/sec — we add 1.1s delay between requests.
  async geocodeChildren(children) {
    const results = []
    const total = children.length

    for (let i = 0; i < total; i++) {
      const child = children[i]

      // Respect Nominatim rate limit: 1.1s delay between requests (skip first)
      if (i > 0) {
        await new Promise(r => setTimeout(r, 1100))
      }

      const remaining = total - i - 1
      const eta = remaining > 0 ? ` (~${remaining}s remaining)` : ""
      this.showLoadingMessage(`Geocoding ${i + 1} of ${total}${eta}`)
      this.showProgress(i + 1, total)

      try {
        const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(child.geocode_query)}&format=json&limit=1&countrycodes=us`
        const resp = await fetch(url)
        const data = await resp.json()
        if (data.length > 0) {
          child.latitude = parseFloat(data[0].lat)
          child.longitude = parseFloat(data[0].lon)
          results.push({ id: child.id, latitude: child.latitude, longitude: child.longitude })
        }
      } catch (e) {
        console.warn(`Geocode failed for ${child.name}:`, e)
      }

      // Save in batches of 20 to persist progress
      if (results.length > 0 && results.length % 20 === 0) {
        this.saveCoordinates(results.slice(results.length - 20))
      }
    }

    // Save any remaining coordinates
    if (results.length > 0) {
      this.saveCoordinates(results)
    }
  }

  // Persist geocoded coordinates so subsequent loads are instant
  saveCoordinates(coords) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch("/api/geographies/update_coordinates", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token
      },
      body: JSON.stringify({ coordinates: coords })
    }).catch(e => console.warn("Failed to save coordinates:", e))
  }

  clearMarkers() {
    this.markers.forEach(m => m.remove())
    this.markers = []
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
